import { createClient } from "@supabase/supabase-js";

// Limpar TODAS as sessões Supabase do localStorage
const cleanAllSupabaseSessions = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Remove todas as chaves Supabase (sb-*, supabase-auth-token, etc)
      if (key && (key.match(/^sb-[a-z0-9]+/) || key.includes('supabase') || key.includes('auth'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log('🧹 Limpando chave antiga:', key);
    });
    console.log(`✅ ${keysToRemove.length} sessões Supabase limpas do localStorage`);
  } catch (error) {
    console.warn('Erro ao limpar sessões:', error);
  }
};

// Limpar sessão de outros projetos Supabase
const cleanOtherSupabaseSessions = (currentProjectId) => {
  try {
    // Limpar chaves do localStorage de projetos Supabase diferentes
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Procura por padrão sb-[projectid]
      if (key && key.match(/^sb-[a-z0-9]+/) && !key.includes(currentProjectId)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log('🧹 Sessão de outro projeto removida:', key);
    });
  } catch (error) {
    console.warn('Erro ao limpar sessões antigas:', error);
  }
};

// Aguardar configuração ser carregada se necessário
const waitForConfig = () => {
  return new Promise((resolve) => {
    if (window.configLoaded || window.ENV?.REACT_APP_SUPABASE_URL) {
      resolve();
    } else {
      const checkConfig = () => {
        if (window.configLoaded || window.ENV?.REACT_APP_SUPABASE_URL) {
          resolve();
        } else {
          setTimeout(checkConfig, 50);
        }
      };
      checkConfig();
    }
  });
};

// Obter configurações do Supabase com fallbacks
const getSupabaseConfig = () => {
  // ✅ Em DESENVOLVIMENTO/STAGE: Priorizar variáveis de ambiente (.env files)
  // ❌ Em PRODUÇÃO (Docker): Usar window.ENV da config.js

  const isDev = process.env.NODE_ENV === 'development' ||
                process.env.NODE_ENV === 'stage' ||
                process.env.REACT_APP_ENV === 'development' ||
                process.env.REACT_APP_ENV === 'stage';

  let supabaseUrl, supabaseAnonKey, source;

  if (isDev) {
    // 🔵 DESENVOLVIMENTO/STAGE: Usar APENAS process.env (variáveis do React)
    supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    source = 'process.env (.env files)';

    console.log('🔵 MODO DESENVOLVIMENTO/STAGE: Usando variáveis de ambiente do React');
  } else {
    // 🔴 PRODUÇÃO: Usar window.ENV se disponível
    supabaseUrl = window.ENV?.REACT_APP_SUPABASE_URL || process.env.REACT_APP_SUPABASE_URL;
    supabaseAnonKey = window.ENV?.REACT_APP_SUPABASE_ANON_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY;
    source = window.ENV?.REACT_APP_SUPABASE_URL ? 'window.ENV (public/config.js)' : 'process.env';

    console.log('🔴 MODO PRODUÇÃO: Usando config.js');
  }

  // Log limpo da configuração apenas se necessário para debug
  if (process.env.REACT_APP_DEBUG === 'true') {
    const projectId = supabaseUrl?.match(/https:\/\/([a-z0-9]+)\.supabase\.co/)?.[1] || "desconhecido";
    console.log('🔍 Supabase Config:', {
      url: supabaseUrl,
      projectId: projectId,
      source: source,
      isDev: isDev,
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_ENV: process.env.REACT_APP_ENV,
      configLoaded: window.configLoaded
    });
  }

  return { supabaseUrl, supabaseAnonKey };
};

// Inicializar cliente Supabase
const initializeSupabase = async () => {
  await waitForConfig();

  const { supabaseUrl, supabaseAnonKey } = getSupabaseConfig();

  if (!supabaseUrl || !supabaseAnonKey) {
    console.error('❌ Configurações do Supabase não encontradas:', {
      url: !!supabaseUrl,
      key: !!supabaseAnonKey,
      windowEnv: !!window.ENV,
      processEnv: !!process.env.REACT_APP_SUPABASE_URL
    });
    throw new Error("Supabase URL and Anon Key must be defined");
  }

  // Extrair project ID da URL do Supabase
  const projectIdMatch = supabaseUrl.match(/https:\/\/([a-z0-9]+)\.supabase\.co/);
  const currentProjectId = projectIdMatch ? projectIdMatch[1] : null;

  console.log(`🔄 Inicializando Supabase para projeto: ${currentProjectId}`);

  // Limpar sessões de projetos Supabase diferentes (ex: mudança de dev → stage)
  if (currentProjectId) {
    cleanOtherSupabaseSessions(currentProjectId);
  }

  if (process.env.REACT_APP_DEBUG === 'true') {
    console.log('✅ Supabase inicializado com:', {
      url: supabaseUrl,
      keyLength: supabaseAnonKey.length,
      source: window.ENV?.REACT_APP_SUPABASE_URL ? 'window.ENV' : 'process.env'
    });
  }

  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,  // ✅ Persistir sessão no localStorage
      autoRefreshToken: true, // ✅ Renovar token automaticamente
    },
  });
};

// Exportar cliente como promise para compatibilidade
let supabaseClient = null;
const getSupabaseClient = async () => {
  if (!supabaseClient) {
    supabaseClient = await initializeSupabase();
  }
  return supabaseClient;
};

// Exportar cliente de forma síncrona com fallback
export const supabase = new Proxy({}, {
  get(target, prop) {
    if (!supabaseClient) {
      // Inicializar de forma síncrona se possível
      const { supabaseUrl, supabaseAnonKey } = getSupabaseConfig();
      // Log apenas em modo debug
      if (process.env.REACT_APP_DEBUG === 'true') {
        console.log('🔍 Criando cliente Supabase para propriedade:', prop);
      }

      if (supabaseUrl && supabaseAnonKey) {
        try {
          // Extrair project ID e limpar sessões antigas
          const projectIdMatch = supabaseUrl.match(/https:\/\/([a-z0-9]+)\.supabase\.co/);
          const currentProjectId = projectIdMatch ? projectIdMatch[1] : null;

          console.log(`🔄 Proxy: Inicializando Supabase para projeto: ${currentProjectId}`);

          // Limpar sessões de projetos diferentes
          if (currentProjectId) {
            cleanOtherSupabaseSessions(currentProjectId);
          }

          supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
            auth: {
              persistSession: true,  // ✅ Persistir sessão no localStorage
              autoRefreshToken: true, // ✅ Renovar token automaticamente
            },
          });
          if (process.env.REACT_APP_DEBUG === 'true') {
            console.log('✅ Cliente Supabase criado com sucesso');
          }
        } catch (error) {
          console.error('❌ Erro ao criar cliente Supabase:', error.message);
          throw error;
        }
      } else {
        console.error('❌ Variáveis do Supabase não disponíveis');
      }
    }
    return supabaseClient?.[prop];
  }
});

// Exportar função para aguardar inicialização completa
export const waitForSupabase = getSupabaseClient;
